/**
 * DailyJournalWidget - Daily overview with journaling interface
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Sun, Moon, Plus, Shuffle, Edit2, CheckCircle, Circle } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import { MoodEnergySelector } from '../EnhancedDayView/components/MoodEnergySelector';
import type { DailyJournalWidgetConfig } from '../../types/widgets';
import type { ReviewEntry } from '../../types';
import { useDays } from '../../hooks/useDays';
import type { DayUpdate } from '../../hooks/useDays';
import { useTodos } from '../../hooks/useTodos';
import { promptsAPI } from '../../services/api';
import { DEFAULT_PROMPT_SETS } from '../../data/prompts';

interface DailyJournalWidgetProps {
  config: DailyJournalWidgetConfig;
  widgetId: string;
  windowId: string;
}

export function DailyJournalWidget({ config, widgetId, windowId }: DailyJournalWidgetProps) {
  const { currentDay, updateDay, startDay, endDay, isLoading } = useDays(new Date());
  const { todos } = useTodos();
  const [expanded, setExpanded] = useState(false);

  // Journal editing state
  const [journalData, setJournalData] = useState<{
    general_reflections: ReviewEntry[];
    priorities_for_day: string[];
    current_mood: string;
    current_energy: string;
    refresh_notes: string;
  }>({
    general_reflections: [],
    priorities_for_day: [],
    current_mood: '',
    current_energy: '',
    refresh_notes: '',
  });
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);
  const [editingPriorityIndex, setEditingPriorityIndex] = useState<number | null>(null);

  // Sync journal data when day data loads or modal opens
  useEffect(() => {
    if (currentDay && expanded) {
      setJournalData({
        general_reflections: currentDay.general_reflections?.length > 0
          ? currentDay.general_reflections
          : DEFAULT_PROMPT_SETS.general.slice(0, 3).map(p => ({ prompt: p, response: '' })),
        priorities_for_day: currentDay.priorities_for_day?.length > 0
          ? currentDay.priorities_for_day
          : [],
        current_mood: currentDay.morning_mood || currentDay.evening_mood || '',
        current_energy: currentDay.morning_energy || currentDay.evening_energy || '',
        refresh_notes: currentDay.refresh_notes || '',
      });
    }
  }, [currentDay, expanded]);

  // Today's task stats
  const todayStr = new Date().toISOString().split('T')[0];
  const todayTasks = todos.filter(todo => {
    const date = todo.deadline;
    if (!date) return false;
    return date.startsWith(todayStr);
  });
  const completedToday = todayTasks.filter(t => t.status === 'completed').length;

  const handleRandomPrompt = useCallback(async (index: number) => {
    try {
      const result = await promptsAPI.getInspirationRandom('general');
      setJournalData(prev => {
        const updated = [...prev.general_reflections];
        updated[index] = { ...updated[index], prompt: result.prompt };
        return { ...prev, general_reflections: updated };
      });
    } catch {
      // Fallback to local prompts
      const prompts = DEFAULT_PROMPT_SETS.general;
      const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
      setJournalData(prev => {
        const updated = [...prev.general_reflections];
        updated[index] = { ...updated[index], prompt: randomPrompt };
        return { ...prev, general_reflections: updated };
      });
    }
  }, []);

  const handleAddEntry = () => {
    setJournalData(prev => ({
      ...prev,
      general_reflections: [...prev.general_reflections, { prompt: 'What\'s on your mind?', response: '' }],
    }));
  };

  const handleSave = async () => {
    if (!currentDay) return;
    const data: DayUpdate = {
      general_reflections: journalData.general_reflections,
      priorities_for_day: journalData.priorities_for_day.filter(p => p.trim()),
      refresh_notes: journalData.refresh_notes,
      current_mood: journalData.current_mood || undefined,
      current_energy: journalData.current_energy || undefined,
    };
    await updateDay(currentDay.id, data);
    setExpanded(false);
  };

  const handleStartDay = async () => {
    if (!currentDay) return;
    await startDay(currentDay.id, {
      priorities_for_day: journalData.priorities_for_day,
      morning_mood: journalData.current_mood || undefined,
      morning_energy: journalData.current_energy || undefined,
    });
  };

  const handleEndDay = async () => {
    if (!currentDay) return;
    await endDay(currentDay.id, {
      evening_mood: journalData.current_mood || undefined,
      evening_energy: journalData.current_energy || undefined,
    });
  };

  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <WidgetWrapper title="Daily Journal" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
        <div className="p-3 h-full overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-20 text-gray-400">
              Loading...
            </div>
          ) : (
            <div className="space-y-3">
              {/* Date and status */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{formattedDate}</span>
                <div className="flex gap-1">
                  {currentDay?.is_day_started && (
                    <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                      Started
                    </span>
                  )}
                  {currentDay?.is_day_ended && (
                    <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                      Ended
                    </span>
                  )}
                </div>
              </div>

              {/* Quick stats */}
              {config.showTasks !== false && (
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    {completedToday}/{todayTasks.length} tasks
                  </span>
                  {config.showMood !== false && currentDay?.morning_mood && (
                    <span>Mood: {currentDay.morning_mood}</span>
                  )}
                </div>
              )}

              {/* Priorities */}
              {currentDay?.priorities_for_day && currentDay.priorities_for_day.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Priorities</h4>
                  <ul className="space-y-0.5">
                    {currentDay.priorities_for_day.slice(0, 3).map((priority, i) => (
                      <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="truncate">{priority}</span>
                      </li>
                    ))}
                    {currentDay.priorities_for_day.length > 3 && (
                      <li className="text-xs text-gray-400 pl-3">+{currentDay.priorities_for_day.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Latest journal entries */}
              {currentDay?.general_reflections && currentDay.general_reflections.some(e => e.response?.trim()) && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Journal</h4>
                  {currentDay.general_reflections.filter(e => e.response?.trim()).slice(0, 2).map((entry, i) => (
                    <div key={i} className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <span className="font-medium">{entry.prompt}:</span>{' '}
                      <span className="line-clamp-2">{entry.response}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                {!currentDay?.is_day_started && (
                  <button
                    onClick={() => setExpanded(true)}
                    className="flex-1 py-1.5 px-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded text-xs font-medium hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors flex items-center justify-center gap-1"
                  >
                    <Sun className="w-3.5 h-3.5" />
                    Start Day
                  </button>
                )}
                <button
                  onClick={() => setExpanded(true)}
                  className="flex-1 py-1.5 px-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center justify-center gap-1"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  Journal
                </button>
                {currentDay?.is_day_started && !currentDay?.is_day_ended && (
                  <button
                    onClick={() => setExpanded(true)}
                    className="flex-1 py-1.5 px-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded text-xs font-medium hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors flex items-center justify-center gap-1"
                  >
                    <Moon className="w-3.5 h-3.5" />
                    End Day
                  </button>
                )}
              </div>

              {/* Empty state */}
              {!currentDay && (
                <div className="flex flex-col items-center justify-center py-4 text-gray-400">
                  <BookOpen className="w-8 h-8 mb-2" />
                  <p className="text-sm">No day data yet</p>
                  <p className="text-xs">Click to start your day</p>
                </div>
              )}
            </div>
          )}
        </div>
      </WidgetWrapper>

      {/* Expanded Journal Modal */}
      <AdaptiveModal isOpen={expanded} onClose={() => setExpanded(false)} title={`Daily Journal - ${formattedDate}`} size="lg">
        <div className="space-y-6">
          {/* Day status actions */}
          {currentDay && (
            <div className="flex items-center gap-2">
              {!currentDay.is_day_started ? (
                <button
                  onClick={handleStartDay}
                  className="px-3 py-1.5 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 text-sm flex items-center gap-1"
                >
                  <Sun className="w-4 h-4" />
                  Start Day
                </button>
              ) : (
                <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                  Day Started
                </span>
              )}
              {currentDay.is_day_started && !currentDay.is_day_ended && (
                <button
                  onClick={handleEndDay}
                  className="px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-sm flex items-center gap-1"
                >
                  <Moon className="w-4 h-4" />
                  End Day
                </button>
              )}
              {currentDay.is_day_ended && (
                <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                  Day Ended
                </span>
              )}
            </div>
          )}

          {/* Priorities for today */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Priorities for Today</h3>
            <div className="space-y-2">
              {journalData.priorities_for_day.map((priority, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-xs text-gray-500 font-medium">{index + 1}.</span>
                  {editingPriorityIndex === index ? (
                    <input
                      type="text"
                      value={priority}
                      onChange={(e) => setJournalData(prev => {
                        const updated = [...prev.priorities_for_day];
                        updated[index] = e.target.value;
                        return { ...prev, priorities_for_day: updated };
                      })}
                      onBlur={() => setEditingPriorityIndex(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setEditingPriorityIndex(null);
                      }}
                      className="flex-1 px-2 py-1 border border-blue-300 dark:border-blue-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={() => setEditingPriorityIndex(index)}
                      className="flex-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      {priority || 'Click to edit...'}
                    </span>
                  )}
                  <button
                    onClick={() => setJournalData(prev => ({
                      ...prev,
                      priorities_for_day: prev.priorities_for_day.filter((_, i) => i !== index),
                    }))}
                    className="text-gray-400 hover:text-red-500 p-1"
                    type="button"
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  setJournalData(prev => ({
                    ...prev,
                    priorities_for_day: [...prev.priorities_for_day, ''],
                  }));
                  setEditingPriorityIndex(journalData.priorities_for_day.length);
                }}
                className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
                type="button"
              >
                <Plus className="w-3 h-3" />
                Add priority
              </button>
            </div>
          </div>

          {/* Journal prompts */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Journal Prompts</h3>
            <div className="space-y-4">
              {journalData.general_reflections.map((entry, index) => (
                <div key={index} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    {editingPromptIndex === index ? (
                      <input
                        type="text"
                        value={entry.prompt}
                        onChange={(e) => setJournalData(prev => {
                          const updated = [...prev.general_reflections];
                          updated[index] = { ...updated[index], prompt: e.target.value };
                          return { ...prev, general_reflections: updated };
                        })}
                        onBlur={() => setEditingPromptIndex(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setEditingPromptIndex(null);
                        }}
                        className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1 mr-2"
                        autoFocus
                      />
                    ) : (
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex-1">
                        {entry.prompt}
                      </label>
                    )}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleRandomPrompt(index)}
                        className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                        title="Get random prompt"
                        type="button"
                      >
                        <Shuffle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingPromptIndex(index)}
                        className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                        title="Edit prompt"
                        type="button"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setJournalData(prev => ({
                          ...prev,
                          general_reflections: prev.general_reflections.filter((_, i) => i !== index),
                        }))}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove prompt"
                        type="button"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={entry.response}
                    onChange={(e) => setJournalData(prev => {
                      const updated = [...prev.general_reflections];
                      updated[index] = { ...updated[index], response: e.target.value };
                      return { ...prev, general_reflections: updated };
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    rows={3}
                    placeholder="Write your thoughts..."
                  />
                </div>
              ))}
              <button
                onClick={handleAddEntry}
                className="flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-700 font-medium"
                type="button"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </button>
            </div>
          </div>

          {/* Mood & Energy */}
          {config.showMood !== false && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Mood & Energy</h3>
              <MoodEnergySelector
                mood={journalData.current_mood}
                energy={journalData.current_energy}
                onMoodChange={(mood) => setJournalData(prev => ({ ...prev, current_mood: mood }))}
                onEnergyChange={(energy) => setJournalData(prev => ({ ...prev, current_energy: energy }))}
              />
            </div>
          )}

          {/* Free-form notes */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Additional Notes</h3>
            <textarea
              value={journalData.refresh_notes}
              onChange={(e) => setJournalData(prev => ({ ...prev, refresh_notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              rows={4}
              placeholder="Any additional thoughts, observations, or notes..."
            />
          </div>

          {/* Today's tasks */}
          {config.showTasks !== false && todayTasks.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Today's Tasks ({completedToday}/{todayTasks.length})
              </h3>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {todayTasks.map(todo => (
                  <li key={todo.id} className="flex items-center gap-2 text-sm py-0.5">
                    {todo.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={todo.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}>
                      {todo.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Save/Cancel buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setExpanded(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Journal
            </button>
          </div>
        </div>
      </AdaptiveModal>
    </>
  );
}
